// Client wrapper for the Roth-for-Alt solver.
//
// The solver and the tax engine it depends on run SERVER-SIDE in the `solve-roth`
// edge function — they are never bundled into the browser. This file only posts
// JSON and returns the result, so no engine math reaches the client bundle.
//
// The pure solver algorithm (and its unit tests) live in
// supabase/functions/solve-roth/solver.ts.

import { supabase } from '@/lib/safeSupabase';
import type { TaxScenarioInput } from './types';

export interface SolveResult {
  /** Smallest Roth conversion that makes Alt fully deductible. null if unachievable within the hard ceiling. */
  roth: number | null;
  /** Largest Alt any Roth below the hard ceiling can support. */
  maxAchievableAlt: number;
}

export interface BracketSolveResult {
  /**
   * Largest Roth conversion keeping ordinary taxable income ≤ (bracket top − buffer).
   * 0 when there's no room left; null when the client is already above the target at $0 conversion.
   */
  roth: number | null;
  /** The solved-against target: top of the chosen bracket minus the buffer. */
  ceiling: number;
  /** Ordinary taxable income at the solved conversion (at $0 conversion when roth is null). */
  ordinaryTaxableIncome: number;
  /** Only with `autoMaxAlt`: the re-maxed Alt the fill was scored against — write it with the conversion. */
  altAmount?: number;
}

async function invokeSolveRoth<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('solve-roth', { body });
  if (error) {
    let msg = error.message;
    try {
      const errBody = await (error as { context?: { json: () => Promise<{ error?: string }> } }).context?.json();
      if (errBody?.error) msg = errBody.error;
    } catch {
      /* keep original message */
    }
    throw new Error(msg);
  }
  return data as T;
}

export async function solveRothForAlt(
  input: TaxScenarioInput,
  opts: { maxIterations?: number; toleranceUsd?: number } = {},
): Promise<SolveResult> {
  return invokeSolveRoth<SolveResult>({ input, opts });
}

/** The deployed solve-roth function predates bracket solving (deploy skew) — retry after the next deploy. */
export class BracketSolverUnavailableError extends Error {
  constructor() {
    super('Fill to Bracket is not available on the server yet — try again after the latest deploy finishes.');
    this.name = 'BracketSolverUnavailableError';
  }
}

/**
 * Fill-to-bracket: largest Roth conversion that keeps the ordinary slice of taxable
 * income inside the chosen federal bracket, minus an optional advisor-typed buffer.
 * Runs server-side in the same `solve-roth` edge function. Pass `autoMaxAlt` when the
 * card's Auto-max Alt is on so the solve scores each probe with the Alt re-maxed —
 * otherwise the post-fill re-snap grows the deduction and the fill lands under target.
 */
export async function solveRothToBracket(
  input: TaxScenarioInput,
  bracket: { rate: number; bufferUsd?: number; autoMaxAlt?: boolean },
): Promise<BracketSolveResult> {
  const data = await invokeSolveRoth<Partial<BracketSolveResult>>({ input, bracket });
  // A pre-bracket deployment of solve-roth silently strips the unknown `bracket` body key
  // and answers the legacy Roth-for-Alt question ({roth, maxAchievableAlt}) instead.
  // Validate the shape so deploy skew surfaces as a clear error, never NaN in the UI.
  const validShape =
    !!data &&
    typeof data.ceiling === 'number' &&
    typeof data.ordinaryTaxableIncome === 'number' &&
    (data.roth === null || typeof data.roth === 'number') &&
    (data.altAmount === undefined || typeof data.altAmount === 'number');
  if (!validShape) {
    throw new BracketSolverUnavailableError();
  }
  return data as BracketSolveResult;
}
